// Beziehungen zwischen Kontakten (Tabelle contact_relations).
//
// Früher gab es genau eine: "ContactProfile".guardian_contact_id (Kind →
// Erziehungsberechtigte:r), nur mit Schalter „Eltern & Kinder“ sichtbar. Ein
// Kind konnte nur EINEN Elternteil haben, und alles andere — die Tochter, die
// für ihre Mutter im Pflegeheim bucht; die Partnerin; die Firma, die für ihre
// Mitarbeiter bucht — ließ sich nicht abbilden. Beim Buchen „nur für andere“
// wurde der Besteller sogar immer als Erziehungsberechtigte:r eingetragen.
//
// Richtung: `from` steht in der Beziehung `kind` zu `to`.
//   guardian   from ist erziehungsberechtigt für to (Elternteil → Kind)
//   relative   from ist Angehörige:r von to (Tochter → Mutter im Heim);
//              `detail` sagt, was genau (parent, child, sibling …)
//   partner    Partner:in (symmetrisch, einmal gespeichert)
//   companion  Begleitung / Freund:in
//   colleague  bucht dienstlich für to (Firma/Kolleg:in → Mitarbeiter:in)
//   other      sonstige

export const CONTACT_RELATION_KINDS = ['guardian', 'relative', 'partner', 'companion', 'colleague', 'other'] as const;
export type ContactRelationKind = (typeof CONTACT_RELATION_KINDS)[number];

/** Beschriftung aus Sicht von `from` („Maria ist … von Paul“) und von `to`. */
export const RELATION_LABELS: Record<ContactRelationKind, { from: string; to: string }> = {
  guardian: { from: 'Erziehungsberechtigt für', to: 'Kind von' },
  relative: { from: 'Angehörige:r von', to: 'Angehörige:r' },
  partner: { from: 'Partner:in', to: 'Partner:in' },
  companion: { from: 'Begleitung von', to: 'Begleitung' },
  colleague: { from: 'bucht dienstlich für', to: 'Mitarbeiter:in von' },
  other: { from: 'Verbunden mit', to: 'Verbunden mit' },
};

/**
 * Was ein Besucher beim Buchen wählen kann: „Ich buche für mein …“. Jede Wahl
 * ergibt eine Beziehung Besteller (from) → Teilnehmer (to).
 */
export const BOOKING_RELATION_CHOICES = {
  child: { label: 'Kind', kind: 'guardian', detail: null },
  parent: { label: 'Elternteil', kind: 'relative', detail: 'parent' },
  relative: { label: 'Angehörige:r', kind: 'relative', detail: null },
  partner: { label: 'Partner:in', kind: 'partner', detail: null },
  companion: { label: 'Begleitung / Freund:in', kind: 'companion', detail: null },
  employee: { label: 'Mitarbeiter:in', kind: 'colleague', detail: null },
  other: { label: 'Andere Person', kind: 'other', detail: null },
} as const satisfies Record<string, { label: string; kind: ContactRelationKind; detail: string | null }>;

export type BookingRelationChoice = keyof typeof BOOKING_RELATION_CHOICES;
export const BOOKING_RELATION_CHOICE_KEYS = Object.keys(BOOKING_RELATION_CHOICES) as BookingRelationChoice[];

export function isBookingRelationChoice(v: unknown): v is BookingRelationChoice {
  return typeof v === 'string' && v in BOOKING_RELATION_CHOICES;
}
